import { EmployeeIdForm } from '../EmployeeIdForm';
import EnterCustomIDEmailForm from '../EnterCustomIDEmailForm';
import EnterPasswordForm from '../EnterPasswordForm';
import EnterWorkDetailsForm from '../EnterWorkDetailsForm';
import EnterWorkEmailForm from '../EnterWorkEmailForm';
import NameForm from '../NameForm';
import SignUpPrivacyConsentForm from '../SignUpPrivacyConsentForm';
import { ComponentProps, RegistrationFlowState } from './types';

export enum AccountVerificationSubRoutes {
  EmployeeId = 'employeeId',
  Email = 'email',
  WorkEmail = 'workEmail',
  Confirmation = 'confirmation',
}

export enum AccountCreationSubRoutes {
  Name = 'name',
  WorkDetails = 'work-details',
  PrivacyConsent = 'privacy-consent',
  Password = 'password',
}

export type SubRoutes = AccountVerificationSubRoutes | AccountCreationSubRoutes;

export enum RegistrationMethod {
  OPEN_EMAIL = 'OPEN_EMAIL',
  CLOSED_EMPLOYEE_ID = 'CLOSED_EMPLOYEE_ID',
  SSO = 'SSO',
}

export enum RegistrationFlow {
  VERIFICATION_CLOSED_EMPLOYEE_ID = 'verification-closed-employee-id',
  VERIFICATION_OPEN_EMAIL = 'verification-open-email',
  ACCOUNT_CREATION = 'account-creation',
  INVITED_USER_SIGNUP = 'invited-user-signup',
}

/**
 * Defines the required fields and default values for each registration flow type.
 */
export const SignUpFlowValidationRules: {
  [K in RegistrationFlow]: {
    requiredFields: (keyof RegistrationFlowState[K])[]; // Fields required for validation
    defaultValues: RegistrationFlowState[K]; // Default values for the state
  };
} = {
  [RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID]: {
    requiredFields: ['employeeId', 'email'],
    defaultValues: {
      employeeId: '',
      email: '',
    },
  },
  [RegistrationFlow.VERIFICATION_OPEN_EMAIL]: {
    requiredFields: ['email'],
    defaultValues: {
      email: '',
    },
  },
  [RegistrationFlow.ACCOUNT_CREATION]: {
    requiredFields: [
      'firstName',
      'lastName',
      'password',
      'explicitPrivacyConsentGiven',
      'marketingOptIn',
      'explicitEEADataStorageConsentGiven',
    ],
    defaultValues: {
      firstName: '',
      lastName: '',
      location: '',
      department: '',
      password: '',
      explicitPrivacyConsentGiven: false,
      marketingOptIn: false,
      explicitEEADataStorageConsentGiven: false,
    },
  },
  [RegistrationFlow.INVITED_USER_SIGNUP]: {
    requiredFields: [
      'email',
      'firstName',
      'lastName',
      'password',
      'explicitPrivacyConsentGiven',
      'marketingOptIn',
      'explicitEEADataStorageConsentGiven',
    ],
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      explicitPrivacyConsentGiven: false,
      marketingOptIn: false,
      explicitEEADataStorageConsentGiven: false,
    },
  },
};

const EmployeeIdFormComponent = {
  route: AccountVerificationSubRoutes.EmployeeId,
  component: EmployeeIdForm,
  fields: ['employeeId'],
};

const EmailFormComponent = {
  route: AccountVerificationSubRoutes.Email,
  component: EnterCustomIDEmailForm,
  fields: ['email'],
};

const WorkEmailFormComponent = {
  route: AccountVerificationSubRoutes.WorkEmail,
  component: EnterWorkEmailForm,
  fields: ['email'],
};

const NameFormComponent = {
  route: AccountCreationSubRoutes.Name,
  component: NameForm,
  fields: ['firstName', 'lastName'],
};

const WorkDetailsFormComponent = {
  route: AccountCreationSubRoutes.WorkDetails,
  component: EnterWorkDetailsForm,
  fields: ['location', 'department'],
};

const PrivacyConsentFormComponent = {
  route: AccountCreationSubRoutes.PrivacyConsent,
  component: SignUpPrivacyConsentForm,
  fields: [
    'explicitPrivacyConsentGiven',
    'marketingOptIn',
    'explicitEEADataStorageConsentGiven',
  ],
};

const PasswordFormComponent = {
  route: AccountCreationSubRoutes.Password,
  component: EnterPasswordForm,
  fields: ['password'],
};

/**
 * Defines the order of sub routes for each registraion flow and components to render at each route.
 */
export const RegistrationMethodMap: {
  [K in RegistrationFlow]: {
    route: SubRoutes;
    component: React.ComponentType<ComponentProps<RegistrationFlowState[K]>>;
    fields: string[];
  }[];
} = {
  [RegistrationFlow.VERIFICATION_CLOSED_EMPLOYEE_ID]: [
    EmployeeIdFormComponent,
    EmailFormComponent,
  ],
  [RegistrationFlow.VERIFICATION_OPEN_EMAIL]: [WorkEmailFormComponent],
  [RegistrationFlow.ACCOUNT_CREATION]: [
    NameFormComponent,
    WorkDetailsFormComponent,
    PrivacyConsentFormComponent,
    PasswordFormComponent,
  ],
  [RegistrationFlow.INVITED_USER_SIGNUP]: [
    EmailFormComponent,
    NameFormComponent,
    PrivacyConsentFormComponent,
    PasswordFormComponent,
  ],
};

export enum TrackedEntryPoint {
  MembershipId = 'membership-id',
  Manual = 'manual',
}

export enum TrackedAuthType {
  Credentials = 'credentials',
  MixedMode = 'mixed-mode',
}

export enum TrackedVerificationStep {
  EmployeeId = 'employee-id',
  Email = 'email',
  WorkEmail = 'work-email',
  ConfirmIdentity = 'confirm-identity',
}

export enum TrackedAccountCreationStep {
  Name = 'name',
  WorkDetails = 'work-details',
  PrivacyConsent = 'privacy-consent',
  Password = 'password',
}

export enum TrackedOutcome {
  SignUpLinkSent = 'sign-up-link-sent',
  SignInLinkSent = 'sign-in-link-sent',
}

export enum TrackedFailureReason {
  InvalidLink = 'invalid-link',
  RateLimited = 'rate-limited',
  Unknown = 'unknown',
}

/**
 * GraphQL error names the `confirmUserAccount` mutation is known to reject
 * with. Lives here rather than beside the mutation so both the error copy and
 * the failure tracking can read it without importing each other.
 */
export enum ConfirmUserAccountError {
  OrganisationInactive = 'OrganisationInactiveError',
}

/**
 * One value per failure branch in `useConfirmUserAccount`: a rejected mutation
 * maps to `OrganisationInactive`, `RateLimited`, or `Unknown`, while a resolved
 * mutation reporting `success: false` maps to `UnsuccessfulResponse`.
 */
export enum TrackedAccountCreationFailureReason {
  OrganisationInactive = 'organisation-inactive',
  RateLimited = 'rate-limited',
  UnsuccessfulResponse = 'unsuccessful-response',
  Unknown = 'unknown',
}

export enum TrackedRegistrationMethod {
  OpenEmail = 'open-email',
  ClosedEmployeeId = 'closed-employee-id',
}

export const TRACKED_REGISTRATION_METHODS: Record<
  RegistrationMethod.OPEN_EMAIL | RegistrationMethod.CLOSED_EMPLOYEE_ID,
  TrackedRegistrationMethod
> = {
  [RegistrationMethod.OPEN_EMAIL]: TrackedRegistrationMethod.OpenEmail,
  [RegistrationMethod.CLOSED_EMPLOYEE_ID]:
    TrackedRegistrationMethod.ClosedEmployeeId,
};

/**
 * Doubles as the allowlist of which routes are verification steps: the account
 * creation subroutes and the confirmation screen deliberately have no entry.
 */
export const VERIFICATION_STEP_NAMES: Partial<
  Record<SubRoutes, TrackedVerificationStep>
> = {
  [AccountVerificationSubRoutes.EmployeeId]: TrackedVerificationStep.EmployeeId,
  [AccountVerificationSubRoutes.Email]: TrackedVerificationStep.Email,
  [AccountVerificationSubRoutes.WorkEmail]: TrackedVerificationStep.WorkEmail,
};

/**
 * The counterpart to VERIFICATION_STEP_NAMES, and likewise the allowlist of
 * which routes are account creation steps. The step names happen to match the
 * route segments today; going through the map keeps the reported name stable if
 * a route is ever renamed.
 */
export const ACCOUNT_CREATION_STEP_NAMES: Partial<
  Record<SubRoutes, TrackedAccountCreationStep>
> = {
  [AccountCreationSubRoutes.Name]: TrackedAccountCreationStep.Name,
  [AccountCreationSubRoutes.WorkDetails]:
    TrackedAccountCreationStep.WorkDetails,
  [AccountCreationSubRoutes.PrivacyConsent]:
    TrackedAccountCreationStep.PrivacyConsent,
  [AccountCreationSubRoutes.Password]: TrackedAccountCreationStep.Password,
};
